import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { Timesheet, User } from '../types';
import { useAuth } from '../contexts/AuthContext';

const startOfWeek = (d: Date) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.setDate(diff));
};

const addDays = (d: Date, days: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
};

const TimesheetCalendar: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [loading, setLoading] = useState(false);
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const isApprover = useMemo(() => user?.role && ['admin', 'super_manager', 'hr_admin', 'manager'].includes(user.role), [user?.role]);

  useEffect(() => {
    const load = async () => {
      try {
        if (isApprover) {
          const list = await apiService.getAllUsers();
          setUsers(list || []);
          if (!selectedUserId && list && list.length > 0) {
            setSelectedUserId(list[0].id);
          }
        }
      } catch {}
    };
    load();
  }, [isApprover]);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const from = weekStart.toISOString().split('T')[0];
        const to = addDays(weekStart, 6).toISOString().split('T')[0];
        const list = await apiService.getTimesheets({ fromDate: from, toDate: to, userId: isApprover ? selectedUserId : user?.id });
        setTimesheets(list || []);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [weekStart, selectedUserId, isApprover, user?.id]);

  const days = Array.from({ length: 7 }).map((_, i) => addDays(weekStart, i));

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" fontWeight={700}>Timesheet Calendar</Typography>
          <DatePicker
            label="Week"
            value={weekStart}
            onChange={(v) => v && setWeekStart(startOfWeek(v as any))}
          />
          {isApprover && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>User</InputLabel>
              <Select label="User" value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value as string)}>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {days.map((d) => {
              const dayStr = d.toISOString().split('T')[0];
              const entries = timesheets.flatMap(ts => (ts.items || [])).filter((it: any) => it.date === dayStr);
              const total = entries.reduce((sum: number, it: any) => sum + (it.hours || 0), 0);
              return (
                <Grid item xs={12} md={6} lg={3} key={dayStr}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6">{d.toDateString()}</Typography>
                      <Typography variant="body2" color="text.secondary">Total: {total}h</Typography>
                      {entries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No entries</Typography>
                      ) : (
                        <Box sx={{ mt: 1 }}>
                          {entries.slice(0, 5).map((it: any, idx: number) => (
                            <Typography key={idx} variant="body2">• {it.projectName || it.projectId}: {it.hours}h {it.description ? `- ${it.description}` : ''}</Typography>
                          ))}
                          {entries.length > 5 && (
                            <Typography variant="caption" color="text.secondary">+{entries.length - 5} more…</Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Layout>
  );
};

export default TimesheetCalendar;


